/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IGdnInvoicesListConfig, IGdnInvoicesListResources } from '../gdn-invoices-list.props.autogenerated';

interface IMultiplePaymentsModalProps {
    className: string;
    isModalOpen: boolean;
    invoiceIds: string[];
    toggle(): void;
    resources: IGdnInvoicesListResources;
    config: IGdnInvoicesListConfig;
}

export const MultiplePaymentsModal: React.FC<IMultiplePaymentsModalProps> = ({
    className,
    isModalOpen,
    invoiceIds,
    toggle,
    resources,
    config
}) => {
    const focusDelay = 110;
    const focusElement = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        if (isModalOpen && focusElement.current) {
            setTimeout(() => {
                focusElement.current?.focus();
            }, focusDelay);
        }
    }, [isModalOpen]);

    if (!ArrayExtensions.hasElements(invoiceIds)) {
        return null;
    }

    const { multiplePaymentsModalBodyFallbackLabel, multiplePaymentsModalHeaderFallbackLabel, okButtonLabel } = resources;

    const modalHeaderLabel =
        config.payAllDueInvoiceModalLabels?.multiplePaymentsModalHeaderLabel ?? multiplePaymentsModalHeaderFallbackLabel;
    const modalBodyLabel = config.payAllDueInvoiceModalLabels?.multiplePaymentsModalBodyLabel ?? multiplePaymentsModalBodyFallbackLabel;
    const modalButtonLabel = config.payAllDueInvoiceModalLabels?.okButtonLabel ?? okButtonLabel;

    return (
        <Modal
            className={classnames(`${className}__multiplePayments__modal`, 'msc-modal-input-required')}
            isOpen={isModalOpen}
            horizontalPosition='center'
            verticalPosition='center'
            autoFocus
            fade={false}
            toggle={toggle}
            aria-label={modalHeaderLabel}
        >
            <ModalHeader tag='h2' toggle={toggle}>
                {modalHeaderLabel}
            </ModalHeader>

            <ModalBody>
                <div className={`${className}__multiplePayments__modalBody`}>
                    <p className={`${className}__multiplePayments__message`}>{modalBodyLabel}</p>

                    <div className={`${className}__multiplePayments__listWrap`} aria-label={modalBodyLabel}>
                        <ul className={`${className}__multiplePayments__invoiceList`}>
                            {invoiceIds.map(id => (
                                <li key={id} className={`${className}__multiplePayments__invoiceItem`}>
                                    <span className={`${className}__multiplePayments__invoiceId`}>{id}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </ModalBody>

            <ModalFooter>
                <button ref={focusElement} className={`${className}__multiplePayments__okButton`} onClick={toggle} type='button'>
                    {modalButtonLabel}
                </button>
            </ModalFooter>
        </Modal>
    );
};
